import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { useOrder } from "./useOrder";
import { useDiscount } from "./useDiscount";
import { usePluginspace } from "./usePluginspace";
import { getPlugintypeIdByCode } from "../functions/plugintype";
import { floatToCents } from "../functions/price_functions";
import { cpfCnpjNoMask } from "../functions/mask";
import { useToast } from "./useToast";
import { Product } from "../contexts/cartContext/cartType";
import { ProductType } from "../contexts/OrderContext";
import {
  applyCashbackDiscountToProducts,
  calculateCashbackRedeem,
  getAvailableCashbackWallet,
  mapProductsToRedeemPayload,
  removeCashbackFromProducts,
} from "../pages/NewCheckoutCart/factory/cashbackHelper";
import { useCashbackFeature } from "./useCashbackFeature";

type UseFoodCashbackRedeemOptions = {
  enabled?: boolean;
};

function toCartProducts(products: ProductType[]): Product[] {
  return products as unknown as Product[];
}

function toOrderProducts(products: Product[]): ProductType[] {
  return products as unknown as ProductType[];
}

/**
 * Resgate de cashback para o fluxo Food (OrderContext).
 * Reutiliza as mesmas rotas/helpers da moda; não altera cartContext.
 */
export function useFoodCashbackRedeem(
  options: UseFoodCashbackRedeemOptions = {},
) {
  const { enabled = true } = options;
  const toastComponent = useToast();
  const { getToken, pluginspaceData } = usePluginspace();
  const {
    order,
    customer,
    freight,
    setProductsOrder,
  } = useOrder();
  const {
    discounts,
    applyCashback,
    removeCashback,
    hasDiscount,
  } = useDiscount();
  const { cashbackFeatureEnabled, cashbackFeatureLoaded } =
    useCashbackFeature();

  const featureEnabled =
    enabled && cashbackFeatureLoaded && cashbackFeatureEnabled;

  const products = order?.products || [];

  const [availableBalance, setAvailableBalance] = useState(0);
  const [walletLoaded, setWalletLoaded] = useState(false);
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [walletLoading, setWalletLoading] = useState(true);
  const [redeemValue, setRedeemValue] = useState("");
  const [loading, setLoading] = useState(false);

  const customerId = Number(customer?.id || 0);
  const customerDocument = cpfCnpjNoMask(customer?.cpf || "");
  const hasCashback = hasDiscount("cashback");
  const appliedAmount = useMemo(() => {
    return Number(discounts.find((d) => d.type === "cashback")?.value || 0);
  }, [discounts]);

  const currentFreight = Number(freight || 0);

  const plugintypeId = useMemo(() => {
    if (!pluginspaceData?.type) return undefined;
    return getPlugintypeIdByCode(pluginspaceData.type) || undefined;
  }, [pluginspaceData?.type]);

  const fetchAvailableBalance = useCallback(async () => {
    if (!cashbackFeatureLoaded) return;

    if (!featureEnabled || !customerId) {
      setAvailableBalance(0);
      setWalletEnabled(false);
      setWalletLoaded(true);
      setWalletLoading(false);
      return;
    }

    try {
      setWalletLoading(true);
      const token = await getToken();
      const result = await getAvailableCashbackWallet(
        customerId,
        api,
        token,
        plugintypeId,
      );

      if (!result.success || !result.data) {
        setAvailableBalance(0);
        setWalletEnabled(false);
        setWalletLoaded(true);
        return;
      }

      const balance = Number(result.data.availableBalance || 0);
      setAvailableBalance(balance);
      setWalletEnabled(true);
      setWalletLoaded(true);
    } catch (error) {
      console.error(error);
      setAvailableBalance(0);
      setWalletEnabled(false);
      setWalletLoaded(true);
    } finally {
      setWalletLoading(false);
    }
  }, [
    cashbackFeatureLoaded,
    customerId,
    featureEnabled,
    getToken,
    plugintypeId,
  ]);

  useEffect(() => {
    if (!cashbackFeatureLoaded) return;
    void fetchAvailableBalance();
  }, [cashbackFeatureLoaded, fetchAvailableBalance]);

  const prepareBaseProducts = useCallback((sourceProducts: ProductType[]) => {
    const asCart = toCartProducts(sourceProducts);
    if (
      asCart.some((p) => p.cashbackDiscount || p.oldCashbackPrice !== undefined)
    ) {
      return toOrderProducts(removeCashbackFromProducts(asCart));
    }
    return sourceProducts;
  }, []);

  const redeemWithAmount = useCallback(
    async (
      amount: number,
      opts?: { silent?: boolean; sourceProducts?: ProductType[] },
    ) => {
      const silent = opts?.silent ?? false;
      if (!featureEnabled || !customerId || !amount || amount <= 0) {
        return false;
      }

      const sourceProducts = opts?.sourceProducts ?? products;
      const baseProducts = prepareBaseProducts(sourceProducts);
      const baseAsCart = toCartProducts(baseProducts);

      const token = await getToken();
      const deliveryTax = floatToCents(currentFreight);
      const orderValue = floatToCents(
        baseProducts.reduce((sum, product) => {
          const price =
            product.promotionalPrice > 0
              ? product.promotionalPrice
              : product.unitPrice;
          return sum + price * Number(product.quantity);
        }, 0),
      );

      const result = await calculateCashbackRedeem(
        {
          customerId,
          redeemAmount: Number(amount.toFixed(2)),
          hasCoupon: hasDiscount("coupon"),
          deliveryTax,
          orderValue,
          plugintypeId,
          products: mapProductsToRedeemPayload(baseAsCart),
        },
        api,
        token,
      );

      if (!result.success || !result.data) {
        if (!silent) {
          toastComponent.openToast(
            "warning",
            result.errorMsg ||
              result.message ||
              "Não foi possível aplicar o cashback",
            6000,
          );
        }
        return false;
      }

      const applied = applyCashbackDiscountToProducts(
        baseAsCart,
        result.data.products || [],
        Number(result.data.appliedAmount || amount),
      );

      if (!applied.success) {
        if (!silent) {
          toastComponent.openToast(
            "warning",
            applied.errorMsg || "Não foi possível aplicar o cashback",
            6000,
          );
        }
        return false;
      }

      await setProductsOrder(toOrderProducts(applied.discountedProducts));
      applyCashback(
        applied.appliedAmount,
        `Resgate de cashback: R$ ${applied.appliedAmount.toFixed(2)}`,
      );
      setRedeemValue("");

      if (!silent) {
        toastComponent.openToast(
          "success",
          `Cashback de R$ ${applied.appliedAmount.toFixed(2)} aplicado`,
          5000,
        );
      }

      await fetchAvailableBalance();
      return true;
    },
    [
      applyCashback,
      currentFreight,
      customerId,
      featureEnabled,
      fetchAvailableBalance,
      getToken,
      hasDiscount,
      plugintypeId,
      prepareBaseProducts,
      products,
      setProductsOrder,
      toastComponent,
    ],
  );

  const clearCashbackRedeem = useCallback(
    async (silent?: boolean) => {
      const hasProductCashback = products.some(
        (p) => p.cashbackDiscount || p.oldCashbackPrice !== undefined,
      );

      if (!hasCashback && !hasProductCashback) return;

      await setProductsOrder(
        toOrderProducts(removeCashbackFromProducts(toCartProducts(products))),
      );
      removeCashback();
      setRedeemValue("");

      if (!silent) {
        toastComponent.openToast("success", "Cashback removido", 4000);
      }

      await fetchAvailableBalance();
    },
    [
      fetchAvailableBalance,
      hasCashback,
      products,
      removeCashback,
      setProductsOrder,
      toastComponent,
    ],
  );

  const previousDocumentRef = useRef<string>("");
  useEffect(() => {
    if (!customerDocument) return;

    if (
      previousDocumentRef.current &&
      previousDocumentRef.current !== customerDocument &&
      hasCashback
    ) {
      void clearCashbackRedeem(true);
    }

    previousDocumentRef.current = customerDocument;
  }, [clearCashbackRedeem, customerDocument, hasCashback]);

  useEffect(() => {
    if (!cashbackFeatureLoaded) return;
    if (!cashbackFeatureEnabled && hasCashback) {
      void clearCashbackRedeem(true);
    }
  }, [
    cashbackFeatureEnabled,
    cashbackFeatureLoaded,
    clearCashbackRedeem,
    hasCashback,
  ]);

  const applyCashbackRedeem = useCallback(async () => {
    if (!customerId) {
      toastComponent.openToast(
        "warning",
        "Identifique o cliente para usar cashback",
        5000,
      );
      return;
    }

    const amount = Number(String(redeemValue).replace(",", "."));
    if (!amount || amount <= 0) {
      toastComponent.openToast(
        "warning",
        "Informe um valor válido de cashback",
        5000,
      );
      return;
    }

    if (amount > availableBalance) {
      toastComponent.openToast(
        "warning",
        "Valor solicitado maior que o saldo disponível",
        5000,
      );
      return;
    }

    try {
      setLoading(true);
      await redeemWithAmount(amount, { silent: false });
    } catch (error) {
      console.error(error);
      toastComponent.openToast("error", "Erro ao aplicar cashback", 5000);
    } finally {
      setLoading(false);
    }
  }, [
    availableBalance,
    customerId,
    redeemValue,
    redeemWithAmount,
    toastComponent,
  ]);

  const shouldShowCashback = Boolean(
    featureEnabled &&
      customerId &&
      walletLoaded &&
      walletEnabled &&
      (availableBalance > 0 || hasCashback),
  );

  return {
    shouldShowCashback,
    cashbackFeatureEnabled: featureEnabled,
    walletLoaded,
    walletLoading,
    walletEnabled,
    availableBalance,
    redeemValue,
    setRedeemValue,
    hasCashback,
    appliedAmount,
    loading,
    applyCashbackRedeem,
    clearCashbackRedeem,
    fetchAvailableBalance,
  };
}
